
    import { Component, Vue, Watch } from 'vue-property-decorator';
    import { namespace, Getter, Mutation, Action } from 'vuex-class';

    import VuePerfectScrollbar from 'vue-perfect-scrollbar';
    import draggable from 'vuedraggable';
    import vSelect from 'vue-select';
    import Multiselect from 'vue-multiselect';

    import { Guard, PatrolZone } from '@/store/dispatch/types';
    import GoogleMap from '@/scripts/mapping/map';
    import { darkMapStyle, satelliteMapStyle, getMapStyle } from '@/scripts/mapping/mapStyle';
    import getScript from '@/services/insertScript.service';
    import intersections from '@/services/intersections.service';
    import api from '@/services/api.service';

    import GuardsCollection from '@/components/dispatch/GuardsCollection.vue';
    import AddNewPatrolZone from '@/components/dispatch/AddNewPatrolZone.vue';
    import NavHeader from '@/components/NavHeader.vue';
	import { GPSService } from "@/services/gps.service";
	import { convertStringToLatLng } from "@sureview/v2-mapping";
    import { UserGroup } from '@/store/types';

    // ambiguos naming, "Dispatch" could mean anything, consider renaming to one of the following:
    // DispatchGuards, GuardDispatch, GuardZones, etc..
    // additionally consider renaming `/dispatch-setup` route
    // make the guards marker draggable and check if it's still inside the zone after moving


    const Dispatch = namespace('dispatch');
    const MapLayers = namespace('mapLayers');

    interface RenderedPatrolZone {
        id?: number;
        isNew: boolean;
        zone: google.maps.Polygon;
        mapLabel?: any;
    }

    interface RenderedMarker {
        guardID: number;
        marker: google.maps.Marker;
    }

    @Component({
        components: {
            draggable,
            'v-select': vSelect,
            'multiselect': Multiselect,
            'vue-perfect-scrollbar': VuePerfectScrollbar,
            'add-new-patrol-zone': AddNewPatrolZone,
            'guards-collection': GuardsCollection,
            'nav-header': NavHeader
        }
    })
    export default class DispatchSetup extends Vue {
        public $refs!: {
            mappingContainer: HTMLElement;
        };

        public isDragging: boolean = false;
        public addingNewZone: boolean = false;
        public isZoneEditing: boolean = false;
        public isZoneDeleting: boolean = false;

        public isMapReady: boolean = false;
        public createdAutocomplete: boolean = false;
        public groupSearchLoading: boolean = false;

        public selectedPatrolZone: any = null;
        public newZone: any = {
            title: '',
            zonePath: null
        };
        public editedZone: PatrolZone | null = null;
        public draggingMarkerStartPosition: google.maps.LatLng | null = null;
        public draggedGuard: any = null;

        public map: GoogleMap | null = null;
        public MapLabel: any = null;
        public Popup: any = null;
        public drawingManager: google.maps.drawing.DrawingManager | null = null;
        public autocomplete: google.maps.places.Autocomplete | null = null;
        public renderedPatrolZones: RenderedPatrolZone[] = [];
        public renderedMarkers: RenderedMarker[] = [];
        public renderedInfoWindows: google.maps.InfoWindow[] = [];
        // https://github.com/SortableJS/Sortable#options
        public dragOptions = {
            animation: 0,
            group: {
                name: 'guards',
                pull: 'clone',
                put: false
            },
            sort: false,
            ghostClass: 'ghost'
        };
        public searchType: string = 'area';
        public selectedGroup: any = {};
        private defaultZoomLevel: number = 20;

        @Getter getPermissions: any;
        @Action logout: () => void;
        @Getter("getMapKey") private mapKey: string;

        @MapLayers.Getter('getMapLayersBounds') MapLayersBounds: google.maps.LatLngBoundsLiteral;
        @MapLayers.Action loadMapLayersBounds: any;
        @MapLayers.Action loadAreaMapLayersBounds: any;

        @Dispatch.Getter('getPatrolZonesList') patrolZones!: PatrolZone[];
        @Dispatch.Getter getPatrolZoneByID: (id: number) => PatrolZone;
        @Dispatch.Getter getGuardByID: (id: number) => Guard;
        @Dispatch.Getter assignedGuards: Guard[];

        @Dispatch.Action updateGuard: any;
        @Dispatch.Action loadPatrolZones: any;
        @Dispatch.Action addPatrolZone: any;
        @Dispatch.Action updatePatrolZone: any;
        @Dispatch.Action deletePatrolZone: any;
        @Dispatch.Action addGuardToPatrolZone: any;
        @Dispatch.Action removeGuardFromPatrolZone: any;
        @Dispatch.Action updateAssignedZones: any;

        @Action searchGroup: any;
        @Getter getHideLabels: any;
        @Getter getMapType: any;
        @Getter('getUserGroups') groups: any;
        @Mutation setMapType: any;

        public get mapPosition() {
            return { lat: 0, lng: 0, zoom: 2 };
        }

        public get polygonOptions() {
            return {
                strokeColor: this.$config.colors.POLYGON_STROKE,
                strokeOpacity: 0.8,
                strokeWeight: 2,
                fillColor: this.$config.colors.POLYGON_FILL,
                fillOpacity: 0.35
            };
        }

        public get newRenderedZone(): RenderedPatrolZone | null {
            return this.renderedPatrolZones.filter(
                (zone: RenderedPatrolZone) => zone.isNew
            )[0];
        }

        public get getRenderedZone() {
            return (id: number): RenderedPatrolZone | null => this.renderedPatrolZones.filter(
                (zone: RenderedPatrolZone) => zone.id === id
            )[0];
        }

        public get getRenderedMarker() {
            return (id: number): RenderedMarker | null => this.renderedMarkers.filter(
                (marker: RenderedMarker) => marker.guardID === id
            )[0];
        }

        public get assignedZonesList() {
            return (guard: Guard) => {
                return Object.keys(guard.patrolZones).map(key => {
                    return { patrolZoneID: Number(key), title: guard.patrolZones[key] };
                });
            }
        }

        async mounted() {
            await this.loadMapLayersBounds();
            await this.loadPatrolZones();
            this.initMap();
        }

        @Watch('addingNewZone')
        onAddingNewZone() {
            this.drawingManager!.setOptions({
                drawingControl: this.addingNewZone,
                drawingMode: this.addingNewZone ?
                    google.maps.drawing.OverlayType.POLYGON :
                    null
            });
        }

        @Watch('patrolZones')
        onPatrolZonesFetch() {
            if (this.isMapReady) {
                this.renderedPatrolZones.forEach(zone => {
                    zone.zone && zone.zone.setMap(null);
                    zone.mapLabel && zone.mapLabel.setMap(null);
                });
                this.renderedPatrolZones = [];
                this.patrolZones.forEach(this.renderZone);
            }
        }

        @Watch('assignedGuards')
        onGuardsListChange() {
            if (this.isMapReady) {
                this.renderedMarkers.forEach(marker => {
                    marker.marker.setMap(null);
                });
                this.$set(this, 'renderedMarkers', []);
                this.assignedGuards.forEach(this.renderGuard);
            }
        }

        @Watch('searchType')
        onSearchTypeChanged(value: string) {
            if (value === 'location' && !this.createdAutocomplete) {
                this.createdAutocomplete = true;
                this.setAutocomplete();
            }
        }

        public initMap() {
            getScript(
                `https://maps.googleapis.com/maps/api/js?key=`
                + `${this.mapKey}`
                + `&libraries=drawing,places,geometry`
            ).then(() => {
                this.MapLabel = require('@/scripts/mapping/mapLabel').default;
                this.Popup = require('@/scripts/mapping/popup').default;
                this.isMapReady = true;

                this.map = new GoogleMap(
                    this.$refs.mappingContainer,
                    this.mapPosition,
                    this.mapPosition.zoom,
                    false, this.getMapType,
                    getMapStyle(this.getMapType, this.getHideLabels)
                );

                if (!this.createdAutocomplete) {
                    this.createdAutocomplete = true;
                    this.setAutocomplete();
                }

                this.map!.registerEvent('onMapTypeChanged', (mapType: string) => {
                    this.setMapType(mapType);
                    this.map!.map.setOptions({
                        styles: getMapStyle(this.getMapType, this.getHideLabels)
                    });
                });

                const bounds = this.getRegionBounds([
                    new google.maps.LatLng(this.MapLayersBounds.south, this.MapLayersBounds.west),
                    new google.maps.LatLng(this.MapLayersBounds.north, this.MapLayersBounds.east)
                ]);

                this.map!.map.fitBounds(bounds);

                this.drawingManager = new google.maps.drawing.DrawingManager({
                    drawingMode: null,
                    drawingControl: this.addingNewZone,
                    drawingControlOptions: {
                        position: google.maps.ControlPosition.TOP_CENTER,
                        drawingModes: ['polygon']
                    },
                    polygonOptions: this.polygonOptions
                } as any);
                this.drawingManager.setMap(this.map!.map);

                // draw existing patrol zones & guards
                this.patrolZones.forEach(this.renderZone);
                this.assignedGuards.forEach(this.renderGuard);

                // on zone add
                google.maps.event.addListener(
                    this.drawingManager,
                    'polygoncomplete',
                    (zone: google.maps.Polygon) => {
                        this.newZone.zonePath = this.getZonePaths(zone);

                        const renderedZone = {
                            isNew: true,
                            zone
                        };
                        zone.setEditable(true); // to make editing possible immediately
                        this.renderedPatrolZones.push(renderedZone);
                        this.addZoneActions(renderedZone);

                        google.maps.event.addListener(zone.getPath(), 'insert_at', () => {
                            this.newZone.zonePath = this.getZonePaths(zone);
                        });
                        google.maps.event.addListener(zone.getPath(), 'set_at', () => {
                            this.newZone.zonePath = this.getZonePaths(zone);
                        });
                    }
                );

                // on guard drop
                this.map!.map.addListener('mouseover', (event: any) => {
                    if (this.draggedGuard) {
                        this.placeGuardMarker(event.latLng, this.draggedGuard);
                        this.draggedGuard = null;
                    }
                });
            });
        }

        public setAutocomplete() {

            setTimeout(() => {
                this.autocomplete = new google.maps.places.Autocomplete(
                    document.getElementById('tbLocation') as HTMLInputElement);
                this.autocomplete!.bindTo('bounds', this.map!.map);
                (this.autocomplete as any).setFields(['address_components', 'geometry', 'name']);
                this.autocomplete!.addListener('place_changed', () => {

                    let place = this.autocomplete!.getPlace();

                    if (place.geometry) {

                        if (place.geometry.viewport) {
                            this.map!.map.fitBounds(place.geometry.viewport);
                        } else if (place.geometry.location) {
                            this.map!.map.setCenter(place.geometry.location);
                            this.map!.map.setZoom(17);
                        }
                    }
                })
            }, 0.5 * this.$config.ANIMATION_DURATION);
        }

        public renderGuard(guard: Guard) {
            try {
                let position: any = JSON.parse(guard.lastLocation!);

                if (typeof position === 'object') {
                    // The constructor also accepts literal objects, and converts them to instances of LatLng:
                    // tslint:disable-next-line
                    position = new google.maps.LatLng(position);
                    const targetZones = this.getMarkerZones(position);
                    const targetZone = targetZones ? targetZones[targetZones.length - 1] : null;
                    const marker = this.renderMarker(position, guard, targetZone!);
                    this.renderedMarkers.push({
                        guardID: guard.guardID!,
                        marker
                    });
                }
            } catch (err) {
                // @todo It's not a LatLng
            }
        }

        public async placeGuardMarker(position: google.maps.LatLng, guard: Guard) {

            const targetZones = this.getMarkerZones(position);
            const targetZone = targetZones ? targetZones[targetZones.length - 1] : null;
            let renderedMarker = this.getRenderedMarker(guard.guardID!);
            const newZonesToAssign = this.getMarkerZones(position);

            if (targetZone) {
                const assignedZones = this.assignedZonesList(guard)
                    .map(zone => this.getRenderedZone(zone.patrolZoneID))
                    .filter(zone => zone);
                const assignedZonesIDs: number[] = assignedZones.map(zone => zone.id);

                if (assignedZones.length) {
                    // if guard is already assigned to some patrol zone
                    // check if there is not just a dragging inside current zone
                    // if it's true - place guard's marker at new position & update guard's location,
                    // Then get a list of zones that contains the location of the marker
                    // Place the marker to the random position near the center of zones intersections
                    // update the list of assigned zones

                    const isIncludePreviousZone = newZonesToAssign
                        .some(zone => assignedZonesIDs.includes(zone.patrolZoneID));
                    const newZonesToAssignPolygons = newZonesToAssign
                        .map(zone => this.getRenderedZone(zone.patrolZoneID).zone);
                    const isReplacingInsideZone = newZonesToAssign.length === 1 &&
                        assignedZonesIDs.length === 1 && isIncludePreviousZone;

                    // Prevent from multiple adding to the same zone just update guard location
                    if (isReplacingInsideZone) {
                        this.updateGuard({...guard, lastLocation: JSON.stringify(position)});
                        return;
                    }

                    if (newZonesToAssign.length > 1) {
                        const target = intersections.getZonesIntersection(newZonesToAssignPolygons);
                        const polygon = intersections.getIntersectionPolygon(target.intersection);
                        position = this.getMarkerRandomLocation(polygon);
                    }

                        // remove guard from every assigned zone
                    await this.deleteGuardFromZone(renderedMarker!, ({} as PatrolZone), true);
                    this.deleteRenderedMarker(guard.guardID);
                }

                const marker = this.renderMarker(position, guard, targetZone);
                this.renderedMarkers.push({
                    guardID: guard.guardID!,
                    marker
                });

                this.updateAssignedZones({
                    guardID: guard.guardID,
                    assignedZones: newZonesToAssign.map(zone => zone.patrolZoneID),
                    lastLocation: JSON.stringify(position)
                });

            } else {
                this.$notify({
                    type: 'error',
                    title: 'Incorrect target',
                    text: 'Please drop the guard inside an existing patrol zone'
                });

                if (this.draggingMarkerStartPosition) {
                    renderedMarker.marker.setPosition(this.draggingMarkerStartPosition);
                    this.draggingMarkerStartPosition = null;
                }
            }

        }

        public getMarkerZones(position: google.maps.LatLng): PatrolZone[] {
            const targetZones = this.renderedPatrolZones.filter(
                (zone: RenderedPatrolZone) => {
                    return google.maps.geometry.poly.containsLocation(position, zone.zone);
                }
            );

            return targetZones.length ? targetZones.map(zone => this.getPatrolZoneByID(zone.id)) : null;
        }

        public deleteZoneMarkers(deletedZoneId: number) {
            this.renderedMarkers.forEach(marker => {
                    const zones = this.getMarkerZones(marker.marker.getPosition()) || [];

                    zones.forEach(zone => {
                        if (zone && zone.patrolZoneID === deletedZoneId) {
                            this.deleteGuardFromZone(marker, zone, false).then(() => {
                                const guard = this.getGuardByID(marker.guardID);
                                // Delete marker if guard has no assigned zone atm
                                // or it was just one zone that contains marker
                                if (zones.length === 1 || Object.keys(guard.patrolZones).length === 0) {
                                    this.deleteRenderedMarker(marker.guardID);
                                }
                            });
                        }
                    });
            });
        }

        public async deleteGuardFromZone(marker: RenderedMarker, zone: PatrolZone, shouldDeleteAssignedZones: boolean) {
            if (!marker)
                return;

            if (shouldDeleteAssignedZones) {
                await this.updateAssignedZones({
                    guardID: marker.guardID,
                    assignedZones: [],
                    lastLocation: null
                });
            } else {
                await this.removeGuardFromPatrolZone({
                    patrolZoneID: zone.patrolZoneID,
                    guardID: marker.guardID
                });
            }
        }

        public addMarkerInfoWindow(marker: google.maps.Marker, zone: PatrolZone, guard: Guard) {
            const contentString = `<p class="text-black">Do you want to remove the guard "${guard.callsign}" from this patrol zone?</p>
      <a class="btn btn-secondary pull-right remove-guard" id="${guard.guardID}">Remove</a>`;
            const guardID = guard.guardID;
            const infoWindow = new google.maps.InfoWindow({
                content: contentString
            });

            infoWindow.open(this.map!.map, marker);

            infoWindow.addListener('domready', () => {
                document
                    .getElementsByClassName('remove-guard')[0]
                    .addEventListener('click', e => {
                        this.deleteGuardFromZone({ guardID, marker }, ({} as PatrolZone)/* zone */, true);
                        this.deleteRenderedMarker(guardID);
                    });
            });

            // Close popups if the user clicks a different guard icon
            this.renderedInfoWindows.forEach(window => {
                window.close();
            });
            this.$set(this, 'renderedInfoWindows', [infoWindow]);
        }

        public addZoneInfoWindow(position: google.maps.LatLng) {
            const contentString = `<p class="text-black">Do you want to remove this patrol zone?</p>
      <a class="btn btn-secondary pull-right" id="remove-zone">Remove</a>`;

            const infoWindow = new google.maps.InfoWindow({
                content: contentString,
                position
            });
            infoWindow.open(this.map!.map);

            return new Promise((resolve, reject) => {
                infoWindow.addListener('domready', () => {
                    document
                        .getElementById('remove-zone')!
                        .addEventListener('click', () => {
                            infoWindow.close();
                            resolve();
                        });
                });
            });
        }

        public renderZone(zone: PatrolZone) {
            const patrolZone = new google.maps.Polygon({
                map: this.map!.map,
                paths: JSON.parse(zone.zonePath),
                ...this.polygonOptions
            });

            const renderedZone: RenderedPatrolZone = {
                id: zone.patrolZoneID,
                zone: patrolZone,
                mapLabel: this.addZoneLabel(patrolZone, zone.title),
                isNew: false
            };

            this.renderedPatrolZones.push(renderedZone);

            this.addZoneActions(renderedZone);
        }

        public renderMarker(position: google.maps.LatLng, guard: Guard, zone: PatrolZone): google.maps.Marker {
            const marker = new google.maps.Marker({
                position,
                map: this.map!.map,
                title: guard.callsign,
                draggable: true,
                icon: this.$config.icons.GUARD
            });

            marker.addListener('click', () => {
                this.addMarkerInfoWindow(marker, zone, guard);
            });

            marker.addListener('dragstart', e => {
                this.draggingMarkerStartPosition = e.latLng;
            });
            marker.addListener('dragend', e => {
                this.placeGuardMarker(e.latLng, this.getGuardByID(guard.guardID));
                this.draggingMarkerStartPosition = null;
            });

            return marker;
        }

        public addZoneActions(zone: RenderedPatrolZone) {
            let popup: google.maps.OverlayView | null;
            const patrolZone = this.getPatrolZoneByID(zone.id);

            if (!patrolZone) {
                return;
            }

            const zoneTitle = patrolZone.title;

            zone.zone.addListener('mouseover', (event) => {
                if (!popup) {
                    // Place a popup at the zone's center

                    const bounds = this.getRegionBounds(zone.zone.getPath());
                    const popupContent = document.getElementById('popupContent').cloneNode(true);
                    (popupContent as any).querySelector('#patrolZoneTitle')!.textContent = zoneTitle;

                    popup = new this.Popup(
                        bounds.getCenter(),
                        popupContent,
                        (e: any) => {
                            this.mapActionMenuClickEventListener(e, zone, popup);
                        }
                    );

                    popup!.setMap(this.map!.map);
                }
            });

            zone.zone.addListener('mouseout', (event) => {
                setTimeout(() => {

                    if (popup) {
                        popup.setMap(null);
                        popup = null;
                    }
                }, 2 * this.$config.ANIMATION_DURATION);
            });
        }

        editCurrentPatrolZone() {
            if (this.selectedPatrolZone) {
                this.isZoneEditing = true;
                this.editedZone = { ...this.selectedPatrolZone };

                var zone = this.renderedPatrolZones.filter((zone) => zone.id == this.selectedPatrolZone.patrolZoneID);
                if (zone.length > 0) {
                    var patrolZone = zone[0];
                    patrolZone.zone.setEditable(!patrolZone.zone.getEditable());
                }

            }
        }

        private mapActionMenuClickEventListener(event: any, zone: RenderedPatrolZone, popup: any) {
            switch (event.target.id) {
                case 'editPatrolZone':
                    this.isZoneEditing = true;
                    this.editedZone = { ...this.getPatrolZoneByID(zone.id) };
                    zone.zone.setEditable(!zone.zone.getEditable());
                    break;

                case 'deletePatrolZone':
                    this.addZoneInfoWindow(popup.position)
                        .then(() => {
                            if (zone.id && !zone.isNew) {
                                this.deletePatrolZone(zone.id);
                                this.deleteZoneMarkers(zone.id);
                            }
                        });
                    break;

                default:
                    break;
            }
        }

        private getZonePaths(zone: google.maps.Polygon) {
            return zone.getPath()
                .getArray()
                .map(point => {
                    return { lat: point.lat(), lng: point.lng() };
                });
        }

        public addZoneLabel(zone: google.maps.Polygon, title: string) {
            const bounds = this.getRegionBounds(zone.getPath());

            const labelPosition = bounds.getSouthWest();

            const mapLabel = new this.MapLabel({
                text: title,
                position: labelPosition,
                map: this.map!.map,
                fontSize: 12,
                align: 'left'
            });
            mapLabel.set('position', labelPosition);

            return mapLabel;
        }

        public onAddNewZone() {
            const zone = { ...this.newZone };
            zone.zonePath = zone.zonePath ? JSON.stringify(zone.zonePath) : '';

            if (zone.title.length && zone.zonePath.length) {
                this.addPatrolZone(zone).then((id: number) => {
                    this.eraseNewZoneModel();
                });
            }
        }

        public onCancelAddNewZone() {
            this.newRenderedZone && this.newRenderedZone.zone.setMap(null);
            this.eraseNewZoneModel();
        }

        public onEditZone() {
            const zone = this.getRenderedZone(this.editedZone!.patrolZoneID);

            if (zone) {
                this.editedZone!.zonePath = JSON.stringify(this.getZonePaths(zone.zone));
                this.updatePatrolZone({
                    id: this.editedZone!.patrolZoneID,
                    data: this.editedZone
                }).then(() => {
                    this.isZoneEditing = false;
                    this.editedZone = null;
                });
            }
        }

        public onDeleteZone() {
            if (this.selectedPatrolZone) {
                this.deletePatrolZone(this.selectedPatrolZone.patrolZoneID);
                this.deleteZoneMarkers(this.selectedPatrolZone.patrolZoneID);

                this.isZoneDeleting = false;
                this.selectedPatrolZone = null;
            }
        }

        public onCancelEditZone() {
            const renderedZone = this.getRenderedZone(this.editedZone!.patrolZoneID);

            if (renderedZone) {
                renderedZone.zone.setEditable(false);
                this.isZoneEditing = false;
                this.editedZone = null;
            }
        }

        public eraseNewZoneModel() {
            this.newZone.title = '';
            this.newZone.zonePath = null;
            this.addingNewZone = false;
        }

        public onAssignedZoneChange({ guardID, zoneIDs }: any) {
            const marker = this.getRenderedMarker(guardID);
            let polygon;
            let newMarkerLocation;

            if (zoneIDs.length > 1) {
                // multiple assigned zones
                const renderedZones = zoneIDs
                    .map((id: number) => this.getRenderedZone(id)!.zone)
                    .filter((zone: RenderedPatrolZone) => zone);

                const target = intersections.getZonesIntersection(renderedZones);

                polygon = target.intersection && intersections.getIntersectionPolygon(target.intersection);

            } else if (zoneIDs.length === 1) {
                // single assigned zone
                polygon = this.getRenderedZone(zoneIDs[0])!.zone;

            }

            newMarkerLocation = polygon ? this.getMarkerRandomLocation(polygon) : '';

            if (marker) {
                if (newMarkerLocation) {
                    marker.marker.setPosition(newMarkerLocation);
                } else {
                    this.deleteRenderedMarker(guardID);
                }
            }

            this.updateAssignedZones({
                guardID,
                assignedZones: zoneIDs,
                lastLocation: JSON.stringify(newMarkerLocation)
            });
        }

        public getMarkerRandomLocation(zone: google.maps.Polygon) {
            const bounds = this.getRegionBounds(zone.getPath());
            const zoneCenter = bounds.getCenter();

            return intersections.getRandomPointFromZoneCenter(zoneCenter, zone, this.map!.map);
        }

        public onFilterSelect(zone: PatrolZone) {
            this.isZoneDeleting = false;
            this.isZoneEditing = false;

            // Center map at bounds
            let paths;
            let renderedZone;

            if (zone) {
                renderedZone = this.getRenderedZone(zone.patrolZoneID!);
            }

            if (zone && renderedZone) {
                paths = renderedZone.zone.getPath();
            } else {
                paths = [
                    new google.maps.LatLng(this.MapLayersBounds.south, this.MapLayersBounds.west),
                    new google.maps.LatLng(this.MapLayersBounds.north, this.MapLayersBounds.east)
                ];
            }

            const bounds = this.getRegionBounds(paths);

            this.map!.map.fitBounds(bounds);
        }

        public getRegionBounds(coords: google.maps.MVCArray<google.maps.LatLng> | google.maps.LatLng[]) {
            const bounds = new google.maps.LatLngBounds();

            (coords as google.maps.LatLng[]).forEach((path: google.maps.LatLng) => {
                bounds.extend(path);
            });
            return bounds;
        }

        public onGuardDragStart(guard: Guard) {
            this.draggedGuard = guard;
            this.isDragging = true;
        }

        public onDeleteGuardFromCollection(id: number) {
            this.deleteRenderedMarker(id);
        }

        public deleteRenderedMarker(id: number) {
            const marker = this.getRenderedMarker(id);
            if (marker) {
                const index = this.renderedMarkers.indexOf(marker);

                marker.marker.setMap(null);
                this.$delete(this.renderedMarkers, index);
            }
        }

        public async areaSelected(area: UserGroup) {
			const bounds = await this.loadAreaMapLayersBounds(area.groupID);
			if (bounds && bounds !== "") {
				this.map!.map.fitBounds(
					new google.maps.LatLngBounds(
						{ lat: bounds.south, lng: bounds.west },
						{ lat: bounds.north, lng: bounds.east }
					)
				);
			} else {
				// if no bounds are returned, try to center on the area based on its lat long
				if (area.latLong && area.latLong !== "") {
					const areaLatLong = convertStringToLatLng(area.latLong);
					this.map!.map.setCenter({lat: areaLatLong.lat, lng: areaLatLong.lng});
					this.map!.map.setZoom(this.defaultZoomLevel);
				} else if (area.address) {
					// if no lat long is provided for this area look it up via the address
					const geoCoder = new GPSService();

					try {
						const geoCodeResponse = await geoCoder.GeoCodeLookup(
							area.address,
							this.mapKey
						);
						if (geoCodeResponse.results && geoCodeResponse.results.length > 0) {
							let addressGeometry = geoCodeResponse.results[0].geometry;

							if (addressGeometry.viewport) {
								this.map!.map.fitBounds(new google.maps.LatLngBounds(
									addressGeometry.viewport.southwest,
									addressGeometry.viewport.northeast
								));
							} else if (addressGeometry.location) {
								this.map!.map.setCenter({lat: addressGeometry.location.lat, lng: addressGeometry.location.lng});
                                this.map!.map.setZoom(this.defaultZoomLevel);
							}
						}
					} catch (err) {
						// we dont need to action anything here.
					}
				}
			}
		}

        public async findGroup(query: string) {
        	if (query && query != "") {
				this.groupSearchLoading = true;
				await this.searchGroup(query);
			}
			this.groupSearchLoading = false;
        }

        public logoutUser() {
          api.logout().then(() => {
              this.logout();
          });
        }

        public locatePatrolZone(patrolZoneID: number) {
            const patrolZone = this.patrolZones.find(x => x.patrolZoneID == patrolZoneID);
            this.onFilterSelect(patrolZone);
        }
    }
